import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon } from 'lucide-react';
export function DiagnosticProcess() {
  const ourApproach = ['Advanced system scanning and live data analysis', 'Manufacturer diagnostic procedures', 'Testing sensors, wiring, and communication signals', 'Physical inspection of related components', 'Confirming the root cause before recommending repairs'];
  return <section id="diagnostic-process" className="py-20 px-4 bg-brand-dark text-white" data-id="element-21">
      <div className="max-w-6xl mx-auto" data-id="element-22">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-23">
          <span className="uppercase tracking-widest text-sm font-bold text-brand-accent mb-4 block" data-id="element-24">
            Our diagnostic approach
          </span>
          <h2 className="font-heading uppercase tracking-wide text-3xl md:text-4xl lg:text-5xl font-extrabold mb-6" data-id="element-25">
            We confirm the cause before replacing parts.
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-26">
            Instead of guessing based on a code, our technicians use advanced
            tools to investigate the entire system so the right repair happens
            the first time.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 mb-16" data-id="element-27">
          {/* Quick Scan Column */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-gray-800/50 border border-gray-700 rounded-2xl p-8 md:p-10 flex flex-col" data-id="element-28">
            <h3 className="font-heading uppercase tracking-wide text-xl font-bold text-gray-400 mb-8 pb-4 border-b border-gray-700" data-id="element-29">
              Typical quick-scan approach
            </h3>
            <div className="flex-grow flex flex-col justify-center space-y-4" data-id="element-30">
              <div className="bg-gray-800 p-4 rounded-xl border border-gray-700 text-center text-gray-300 font-medium" data-id="element-31">
                Read code
              </div>
              <div className="text-center text-gray-600" data-id="element-32">↓</div>
              <div className="bg-gray-800 p-4 rounded-xl border border-gray-700 text-center text-gray-300 font-medium" data-id="element-33">
                Replace the most common part
              </div>
              <div className="text-center text-gray-600" data-id="element-34">↓</div>
              <div className="bg-gray-800 p-4 rounded-xl border border-gray-700 text-center text-gray-300 font-medium" data-id="element-35">
                See if the problem goes away
              </div>
            </div>
          </motion.div>

          {/* Our Approach Column */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-gray-800 border-2 border-brand-accent rounded-2xl p-8 md:p-10 relative shadow-2xl shadow-brand-accent/10" data-id="element-36">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-brand-accent text-brand-dark font-bold px-4 py-1 rounded-full text-sm" data-id="element-37">
              Our approach
            </div>
            <h3 className="font-heading uppercase tracking-wide text-xl font-bold text-white mb-8 pb-4 border-b border-gray-700" data-id="element-38">
              Comprehensive Diagnostics
            </h3>
            <ul className="space-y-6" data-id="element-39">
              {ourApproach.map((item, index) => <li key={index} className="flex items-start gap-4" data-id="element-40">
                  <CheckCircleIcon className="w-6 h-6 text-brand-accent flex-shrink-0 mt-0.5" data-id="element-41" />
                  <span className="text-white font-medium text-lg" data-id="element-42">{item}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-20 space-y-4" data-id="element-43">
          <p className="text-xl text-brand-accent font-semibold" data-id="element-44">
            This process helps ensure parts are replaced only when they actually
            need to be.
          </p>
          <p className="text-lg text-gray-300" data-id="element-45">
            Often the small detail another shop missed is what turns into a much
            larger repair later.
          </p>
        </motion.div>

        {/* Mid-Page CTA */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-brand-dark rounded-2xl p-8 md:p-12 text-center max-w-4xl mx-auto shadow-2xl bg-[#c8b4a4]" data-id="element-46">
          <h3 className="font-heading uppercase tracking-wide text-3xl md:text-4xl font-extrabold mb-6 pl-[0px] pr-[0px]" data-id="element-47">
            Still dealing with a problem other shops couldn't solve?
          </h3>
          <p className="text-xl font-medium mb-4 pl-[0px] pr-[0px]" data-id="element-48">
            Schedule a diagnostic inspection and see exactly what our
            technicians find before approving any repair.
          </p>
          <p className="text-lg mb-8 opacity-90" data-id="element-49">
            Diagnostic inspection fee is applied toward your repair if you move
            forward.
          </p>
          <a href="tel:6512240267" className="inline-flex justify-center items-center px-8 py-4 bg-brand-dark hover:bg-gray-900 text-white font-bold rounded-md transition-colors shadow-lg mb-4" data-id="element-50">
            Call Now to Schedule Your Inspection
          </a>
          <p className="text-sm font-bold opacity-80" data-id="element-51">
            Most appointments are available same day.
          </p>
        </motion.div>
      </div>
    </section>;
}